import StaffTimePicker from "@/components/StaffTimePicker";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { staffApi } from "@/lib/staffApi";
import { ErrorState, Skeleton } from "@/components/BookingShell";
import { formatPrice, formatDuration, formatFullDate, toISODate } from "@/lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DatePicker } from "@/components/ui/calendar";

export function StaffManualBooking() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const prefillDate = params.get("date") || "";
    const prefillTime = params.get("time") || "";
    const prefillDuration = Number(params.get("duration") || 0);
    const allowedServiceIds = useMemo(() => {
        const raw = params.get("services") || "";
        return raw ? raw.split(",").map((n) => Number(n)).filter(Boolean) : null;
    }, [params]);
    const overrideBreak = params.get("override") === "1";
    const [categories, setCategories] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1);
    const [selectedCat, setSelectedCat] = useState(null);
    const [selectedServices, setSelectedServices] = useState([]);
    const selectedSvc = selectedServices.length ? { ...selectedServices[0], name: selectedServices.map(s => s.name).join(' + '), duration: selectedServices.reduce((n,s) => n + Number(s.duration || 0), 0) } : null;
    const serviceKey = selectedServices.map(s => s.id).join(',');
    const [slotError, setSlotError] = useState('');
    const [requestId] = useState(() => globalThis.crypto?.randomUUID?.() || `staff-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const [uncertain, setUncertain] = useState(false);
    const [date, setDate] = useState(prefillDate);
    const [slots, setSlots] = useState(null);
    const [time, setTime] = useState(prefillTime);
    // Sprint 31: user-editable duration. Starts at the calendar's drag duration
    // (or 0 if no prefill). Once a service is chosen and the calendar duration
    // differs from the service's configured duration, we show a clear prompt
    // rather than silently overwriting.
    const [duration, setDuration] = useState(prefillDuration || 0);
    const [customerSearch, setCustomerSearch] = useState("");
    const [customerResults, setCustomerResults] = useState([]);
    const [selectedCust, setSelectedCust] = useState(null);
    const [newCust, setNewCust] = useState(null);
    const [notes, setNotes] = useState("");
    const [notify, setNotify] = useState(true);
    const [depositMode, setDepositMode] = useState('automatic');
    const [busy, setBusy] = useState(false);
    const [submitError, setSubmitError] = useState("");

    useEffect(() => { api.listCategories().then(setCategories).catch(setError); }, []);

    // If the calendar deep-linked us in with a specific service allow-list, only
    // show categories that contain at least one allowed service.
    const filteredCategories = useMemo(() => {
        if (!categories || !allowedServiceIds) return categories;
        return categories
            .map((c) => ({ ...c, services: (c.services || []).filter((s) => allowedServiceIds.includes(Number(s.id))) }))
            .filter((c) => (c.services || []).length > 0);
    }, [categories, allowedServiceIds]);

    useEffect(() => {
        if (!serviceKey || !date) return;
        let active = true;
        setSlots(null); setSlotError('');
        staffApi.bookingSlots(serviceKey.split(',').map(Number), date)
            .then(r => { if (active) setSlots(r.slots || []); })
            .catch(e => { if (active) { setSlots([]); setSlotError(e.message || 'Could not load availability.'); } });
        return () => { active = false; };
    }, [serviceKey, date]);

    useEffect(() => {
        if (!customerSearch) { setCustomerResults([]); return; }
        const t = setTimeout(() => {
            staffApi.listCustomers(customerSearch, 8).then((r) => setCustomerResults(r.customers || [])).catch(() => setCustomerResults([]));
        }, 300);
        return () => clearTimeout(t);
    }, [customerSearch]);

    const submit = async () => {
        if (busy || uncertain) return;
        if (!selectedSvc || !date || !time || (!selectedCust && !newCust)) {
            toast.error("Please complete all steps"); return;
        }
        setBusy(true);
        setSubmitError("");
        try {
            const payload = {
                service_ids: selectedServices.map(s => s.id),
                request_id: requestId,
                date, time,
                internalNotes: notes,
                notifyParticipants: notify,
                deposit_request_mode: depositMode,
            };
            if (selectedCust) payload.customerId = selectedCust.id;
            else payload.customer = newCust;
            const res = await staffApi.manualBooking(payload);
            const apptId = res?.appointment?.id;
            if (!apptId) throw new Error("The booking system did not confirm an appointment number. Check the calendar before trying again.");
            toast.success(`Appointment created${apptId ? ` (#${apptId})` : ""}`);
            if (res.warning) toast.warning(res.warning, { duration: 12000 });
            nav(`/staff/calendar?d=${encodeURIComponent(date)}&v=day`, { replace: true });
        } catch (e) {
            if (!e.status || e.status >= 500) setUncertain(true);
            setSubmitError(e.status && e.status < 500
                ? e.message
                : `${e.message || "Booking could not be confirmed."} Check the calendar before trying again to avoid a duplicate booking.`);
        }
        finally { setBusy(false); }
    };

    const services = useMemo(() => selectedCat?.services || [], [selectedCat]);

    const categoriesForRender = filteredCategories || categories;

    // Sprint 31: computed end-time string for the editable selection block.
    const endTimeStr = useMemo(() => {
        if (!time || !duration) return "—";
        const [h, m] = time.split(":").map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return "—";
        const total = h * 60 + m + Number(duration || 0);
        const eh = Math.floor((total % (24 * 60)) / 60);
        const em = total % 60;
        return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
    }, [time, duration]);

    // Sprint 36.8 — split Date + Time into their own wizard steps so Angela
    // never has to scroll through a completed step. Advance is auto on
    // selection; back navigation preserves earlier choices.
    // Step 1 Category, Step 2 Service, Step 3 Date, Step 4 Time,
    // Step 5 Customer, Step 6 Notes & confirm.
    const goBack = () => {
        // Move back one step, keeping earlier selections intact. Only clear
        // dependent state (time depends on date; slots depend on service+date).
        setStep((s) => {
            const prev = Math.max(1, s - 1);
            if (s === 4) setTime("");
            return prev;
        });
    };

    const stepTitles = ["Category", "Treatment", "Date", "Time", "Customer", "Notes & confirm"];

    return (
        <div className="space-y-5" data-testid="staff-manual-booking">
            <div>
                <div className="flex items-center gap-3">
                    {step > 1 && (
                        <button onClick={goBack} data-testid="mb-back" className="h-9 w-9 rounded-full border hairline flex items-center justify-center text-white/70 hover:text-gold hover:border-gold/50" aria-label="Back">
                            <span className="text-lg leading-none">←</span>
                        </button>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-gold/85">Step {step} of 6</div>
                        <h1 className="font-serif font-light text-3xl sm:text-4xl text-white leading-tight mt-0.5">{stepTitles[step - 1]}</h1>
                    </div>
                </div>
                {/* Compact breadcrumbs of previous selections — quick reference without scrolling */}
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-white/60" data-testid="mb-breadcrumbs">
                    {selectedCat && <button onClick={() => setStep(1)} className="rounded-full border hairline px-2 py-0.5 hover:border-gold/50 hover:text-gold">{selectedCat.name}</button>}
                    {selectedSvc && <button onClick={() => setStep(2)} className="rounded-full border hairline px-2 py-0.5 hover:border-gold/50 hover:text-gold">{selectedSvc.name}</button>}
                    {date && <button onClick={() => setStep(3)} className="rounded-full border hairline px-2 py-0.5 hover:border-gold/50 hover:text-gold">{formatFullDate(date)}</button>}
                    {time && <button onClick={() => setStep(4)} className="rounded-full border hairline px-2 py-0.5 hover:border-gold/50 hover:text-gold">{time}</button>}
                </div>
                {prefillDate && prefillTime && step === 4 && (
                    <StepCard>
                        <p className="text-sm text-white mb-3">Selected from calendar · {selectedSvc?.duration || 0} minutes of treatments</p>
                        <StaffTimePicker label="Start time" value={time} onChange={setTime} data-testid="mb-edit-start" />
                        {time && slots && !slots.some(s => s.time === time) && <p className="text-amber-200 text-sm mt-3">Choose an available start below that fits all treatments.</p>}
                    </StepCard>
                )}
            </div>

            {error && <ErrorState error={error} onRetry={() => window.location.reload()} />}

            {!categories && !error && <Skeleton className="h-64" />}

            {categoriesForRender && (
                <div className="space-y-6">
                    {step === 1 && (
                        <StepCard>
                            {categoriesForRender.length === 0 ? (
                                <div className="text-xs text-white/60" data-testid="mb-no-cats">No services are permitted in the selected working period.</div>
                            ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="mb-cat-grid">
                                {categoriesForRender.map((c) => {
                                    const active = selectedCat?.id === c.id;
                                    return (
                                        <button
                                            key={c.id}
                                            onClick={() => { setSelectedCat(c); setStep(2); }}
                                            data-testid={`mb-cat-${c.id}`}
                                            className={`group w-full min-w-0 rounded-2xl border p-4 text-left transition-all flex items-center gap-4 ${active ? "border-gold bg-gold/10 ring-1 ring-gold/50" : "border-white/15 bg-white/[0.03] hover:border-gold/60 hover:bg-white/[0.06]"}`}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="text-white font-serif text-lg leading-tight break-words">{c.name}</div>
                                                <div className="text-[11px] uppercase tracking-[0.16em] text-gold/80 mt-1">{c.services?.length || 0} services</div>
                                            </div>
                                            <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-colors ${active ? "text-gold" : "text-white/40 group-hover:text-gold"}`} strokeWidth={2} />
                                        </button>
                                    );
                                })}
                            </div>
                            )}
                        </StepCard>
                    )}

                    {step === 2 && selectedCat && (
                        <StepCard>
                            <ul className="space-y-2" data-testid="mb-svc-list">
                                {services.map((s) => {
                                    const active = selectedServices.some(item => item.id === s.id);
                                    return (
                                        <li key={s.id}>
                                            <button
                                                onClick={() => { setSelectedServices(items => active ? items.filter(item => item.id !== s.id) : [...items, s]); }}
                                                aria-pressed={active} data-testid={`mb-svc-${s.id}`}
                                                className={`group w-full text-left rounded-2xl border p-4 flex items-center gap-3 transition-all ${active ? "border-gold bg-gold/10 ring-1 ring-gold/50" : "border-white/15 bg-white/[0.03] hover:border-gold/60 hover:bg-white/[0.06]"}`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-white text-sm font-medium leading-snug break-words">{s.name}</div>
                                                    <div className="text-[11px] uppercase tracking-[0.14em] text-gold/85 mt-1">{formatDuration(s.duration)} · {formatPrice(s.price)}</div>
                                                </div>
                                                <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-colors ${active ? "text-gold" : "text-white/40 group-hover:text-gold"}`} strokeWidth={2} />
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                            <div className="mt-4 space-y-3">
                                <p className="text-sm text-white">{selectedServices.length} treatment{selectedServices.length === 1 ? '' : 's'} selected{selectedSvc ? ` · ${selectedSvc.duration} min` : ''}</p>
                                {selectedServices.map(item => <div key={item.id} className="flex justify-between gap-3 text-sm text-white"><span>{item.name}</span><button type="button" className="text-gold underline" onClick={() => setSelectedServices(items => items.filter(s => s.id !== item.id))}>Remove</button></div>)}
                                <button type="button" onClick={() => setStep(1)} className="block text-gold underline">Add from another category</button>
                                <button type="button" disabled={!selectedServices.length} data-testid="mb-continue-services" onClick={() => { setDuration(selectedSvc.duration); setStep(3); }} className="w-full h-12 rounded-full bg-gold text-navy font-semibold disabled:opacity-40">Continue</button>
                            </div>
                        </StepCard>
                    )}

                    {step === 3 && selectedSvc && (
                        <StepCard>
                            <DateFieldPicker date={date} setDate={(v) => { setDate(v); setTime(''); }} />
                            {date && <button onClick={() => setStep(4)} data-testid="mb-continue-date" className="mt-4 h-11 px-6 rounded-full bg-gold text-navy font-semibold">Continue</button>}
                        </StepCard>
                    )}

                    {step === 4 && selectedSvc && date && (
                        <StepCard>
                            {slotError && <p role="alert" className="text-red-300">{slotError}</p>}
                            {time && slots?.some(s => s.time === time) && <button type="button" onClick={() => setStep(5)} className="mb-4 w-full h-12 rounded-full bg-gold text-navy font-semibold" data-testid="mb-continue-time">Continue at {time}</button>}
                            {!slots ? <Skeleton className="h-20" /> : slots.length === 0 ? (
                                <div className="text-xs text-white/50">No available start times for that day.</div>
                            ) : (
                                <>
                                    <div className="text-[11px] text-white/50 mb-2" data-testid="mb-slot-count">{slots.length} available start{slots.length === 1 ? "" : "s"} — every valid 5-minute start is shown for manual booking.</div>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="mb-slots">
                                        {slots.map((s) => (
                                            <button key={s.iso} onClick={() => { setTime(s.time); setStep(5); }} data-testid={`mb-slot-${s.time}`} className={`h-10 rounded-full border text-xs ${time === s.time ? "bg-gold text-navy border-gold" : "hairline text-white hover:border-gold/60"}`}>{s.time}</button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </StepCard>
                    )}

                    {step === 5 && time && (
                        <StepCard>
                            <input type="text" placeholder="Search existing customer by name or email" value={customerSearch} onChange={(e) => { setCustomerSearch(e.target.value); setSelectedCust(null); setNewCust(null); }} data-testid="mb-cust-search" className="w-full bg-transparent border hairline rounded-full px-4 py-3 text-white text-sm outline-none focus:border-gold" />
                            {customerResults.length > 0 && !selectedCust && (
                                <ul className="mt-2 space-y-1" data-testid="mb-cust-results">
                                    {customerResults.map((c) => (
                                        <li key={c.id}><button onClick={() => { setSelectedCust(c); setNewCust(null); setStep(6); }} data-testid={`mb-cust-pick-${c.id}`} className="w-full text-left rounded-lg border hairline p-2 text-sm text-white hover:border-gold/60">
                                            {c.firstName} {c.lastName} · <span className="text-white/50 text-xs">{c.email}</span>
                                        </button></li>
                                    ))}
                                </ul>
                            )}
                            {selectedCust && (
                                <div className="mt-2 rounded-lg border border-gold/40 p-3 text-sm text-white flex items-center justify-between gap-3" data-testid="mb-selected-customer">
                                    <span>Selected: {selectedCust.firstName} {selectedCust.lastName} · {selectedCust.email}</span>
                                    <button onClick={() => setStep(6)} className="h-9 px-4 rounded-full bg-gold text-navy text-xs font-semibold" data-testid="mb-continue-customer">Continue →</button>
                                </div>
                            )}
                            {!selectedCust && (
                                <details className="mt-4">
                                    <summary className="text-xs text-gold cursor-pointer" data-testid="mb-new-cust-toggle">Or create new customer</summary>
                                    <NewCustomerInline initialValue={newCust} onContinue={(v) => { setNewCust(v); setStep(6); }} />
                                </details>
                            )}
                        </StepCard>
                    )}

                    {step === 6 && (selectedCust || newCust) && (
                        <StepCard>
                            <textarea placeholder="Internal notes (visible to staff only)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mb-notes" rows={3} className="w-full bg-transparent border hairline rounded-2xl p-3 text-white text-sm outline-none focus:border-gold" />
                            <label className="mt-3 flex items-center gap-2 text-sm text-white/70">
                                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="mb-notify" /> Send booking notifications
                            </label>
                            <label className="block text-white text-sm mt-5">
                                Deposit request
                                <select value={depositMode} onChange={e => setDepositMode(e.target.value)} data-testid="mb-deposit-mode" className="mt-2 w-full rounded-2xl bg-white text-slate-900 p-3">
                                    <option value="automatic">Automatically email the deposit request</option>
                                    <option value="manual">Manual — send later or record cash / bank payment</option>
                                </select>
                            </label>
                            <p className="text-white/70 text-sm mt-2">{depositMode === 'automatic'
                                ? 'After saving, email a request for any unpaid service deposit. Services with no deposit are skipped.'
                                : 'No deposit request will be sent. Record money received on the appointment’s payment card; choosing Manual does not mark it paid.'}</p>
                            {submitError && <div role="alert" className="mt-4 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                                {submitError}
                                <button type="button" className="block mt-2 underline font-semibold" onClick={() => nav(`/staff/calendar?d=${encodeURIComponent(date)}&v=day`)}>Check calendar</button>
                            </div>}
                            <button onClick={submit} disabled={busy || uncertain} data-testid="mb-submit" className="mt-6 w-full h-12 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40">
                                {busy ? "Creating..." : uncertain ? "Check calendar before retrying" : "Create appointment"}
                            </button>
                        </StepCard>
                    )}
                </div>
            )}
        </div>
    );
}

function StepCard({ children }) {
    return (
        <section className="rounded-2xl glass p-4 sm:p-5" data-testid="mb-step-card">
            {children}
        </section>
    );
}

function StepSection({ num, title, open, children }) {
    // Sprint 36.8 — kept for backwards-compat but unused by the new wizard.
    return (
        <section className={`rounded-2xl glass p-4 sm:p-5 ${open ? "" : "opacity-40 pointer-events-none"}`} data-testid={`mb-step-${num}`}>
            <div className="text-[10px] uppercase tracking-[0.24em] text-gold/85 mb-2">Step {num}</div>
            <h3 className="font-serif text-xl text-white mb-3">{title}</h3>
            {children}
        </section>
    );
}

/**
 * Sprint 36.11 — Themed date picker for the wizard.
 *
 * The old native <input type=date> rendered as a stark white rectangle
 * against the navy card because Chromium's UA styling for date inputs
 * ignores `bg-transparent` + `text-white` on the value display. Replaced
 * with a shadcn Popover + Calendar that matches the Pout design system
 * exactly (navy trigger, gold accent, calendar icon, formatted label).
 */
function DateFieldPicker({ date, setDate }) {
    const [open, setOpen] = React.useState(false);
    const selected = date ? new Date(`${date}T00:00:00`) : undefined;
    const label = selected
        ? selected.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })
        : "Pick a date";
    const todayISO = toISODate(new Date());
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="mb-date"
                    aria-label="Choose booking date"
                    className={`w-full sm:w-auto min-w-[280px] rounded-2xl border px-4 py-3 text-left flex items-center gap-3 transition-all ${date ? "border-gold/60 bg-gold/10" : "border-white/20 bg-white/[0.03] hover:border-gold/50"}`}
                >
                    <CalendarDays className={`h-5 w-5 flex-shrink-0 ${date ? "text-gold" : "text-white/60"}`} strokeWidth={1.75} />
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Booking date</div>
                        <div className={`text-sm font-medium truncate ${date ? "text-white" : "text-white/60"}`}>{label}</div>
                    </div>
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="app-light w-auto p-3 bg-white border border-navy/15 shadow-lg rounded-xl text-navy" data-testid="mb-date-popover">
                <DatePicker
                    mode="single"
                    selected={selected}
                    onSelect={(d) => {
                        if (!d) return;
                        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                        setDate(iso);
                        setOpen(false);
                    }}
                    defaultMonth={selected || new Date()}
                    disabled={(d) => {
                        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                        return iso < todayISO;
                    }}
                    initialFocus
                    showOutsideDays={true}
                />
            </PopoverContent>
        </Popover>
    );
}


function NewCustomerInline({ initialValue, onContinue }) {
    const [f, setF] = useState(initialValue || { firstName: "", lastName: "", email: "", phone: "", countryPhoneIso: "gb" });
    const valid = f.firstName.trim() && f.lastName.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) && f.phone.replace(/\D/g, '').length >= 7;
    return (
        <div className="mt-3 grid grid-cols-2 gap-3" data-testid="mb-new-cust-form">
            <input placeholder="First name" value={f.firstName} onChange={(e) => setF({...f, firstName: e.target.value})} data-testid="mb-nc-first" className="bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
            <input placeholder="Last name" value={f.lastName} onChange={(e) => setF({...f, lastName: e.target.value})} data-testid="mb-nc-last" className="bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
            <input placeholder="Email" type="email" value={f.email} onChange={(e) => setF({...f, email: e.target.value})} data-testid="mb-nc-email" className="col-span-2 bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
            <input placeholder="Phone" type="tel" value={f.phone} onChange={(e) => setF({...f, phone: e.target.value})} data-testid="mb-nc-phone" className="col-span-2 bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
            <button type="button" disabled={!valid} data-testid="mb-new-client-continue" onClick={() => onContinue(Object.fromEntries(Object.entries(f).map(([k,v]) => [k,v.trim()])))} className="col-span-2 h-12 rounded-full bg-gold text-navy font-semibold disabled:opacity-40">Continue with this client</button>
        </div>
    );
}


/**
 * Sprint 31 — Editable calendar selection block.
 *
 * Angela's drag on the mobile calendar may land 5 minutes off the intended
 * start. Rather than force her back to the calendar, we show a clear editable
 * summary here with quick duration chips, an end-time preview and a live
 * validity check against the day's available slots.
 */
const DURATION_CHIPS = [5, 10, 15, 20, 30, 45, 60];

function SelectionEditor({ date, time, duration, setDate, setTime, setDuration, slots, selectedSvc, overrideBreak, allowedServiceIds }) {
    const [customOpen, setCustomOpen] = useState(false);
    const [customVal, setCustomVal] = useState(String(duration || ""));

    // Compute end time
    const end = useMemo(() => {
        if (!time || !duration) return "";
        const [h, m] = time.split(":").map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return "";
        const total = h * 60 + m + Number(duration);
        const eh = Math.floor((total % (24 * 60)) / 60);
        const em = total % 60;
        return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
    }, [time, duration]);

    // Availability check: if slots have loaded, is the current time still valid?
    const timeAvailable = useMemo(() => {
        if (!slots || slots.length === 0) return null; // unknown until slots load
        return slots.some((s) => s.time === time);
    }, [slots, time]);

    const setCustom = () => {
        const n = Math.max(5, Math.round(Number(customVal || 0) / 5) * 5);
        if (n > 0) setDuration(n);
        setCustomOpen(false);
    };

    return (
        <div className="mt-3 rounded-2xl glass p-4 space-y-3" data-testid="mb-selection-editor">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Booking selection</div>
                    <div className="text-white font-serif text-lg leading-tight mt-1">
                        {date || "—"} · {time || "—:—"} → {end || "—:—"} · {duration || 0} min
                    </div>
                </div>
                {overrideBreak && <span className="text-[10px] uppercase tracking-[0.16em] rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-100 px-2 py-1 font-semibold">Break override</span>}
                {allowedServiceIds && !overrideBreak && <span className="text-[10px] rounded-full border hairline text-white/70 px-2 py-1" data-testid="mb-service-filter-badge">{allowedServiceIds.length} services allowed</span>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-white/70 mb-1 font-semibold">Date</div>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="mb-edit-date" className="w-full text-sm" />
                </label>
                <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-white/70 mb-1 font-semibold">Start time</div>
                    <StaffTimePicker label="Start time" value={time} onChange={setTime} data-testid="mb-edit-start" className="w-full text-sm" />
                </label>
                <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-white/70 mb-1 font-semibold">Ends at</div>
                    <div className="w-full text-sm rounded-md border border-white/25 bg-white/10 px-3 py-2 text-white font-semibold" data-testid="mb-end-preview">{end || "—:—"}</div>
                </label>
            </div>

            <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/70 mb-2 font-semibold">Duration (5-min steps)</div>
                <div className="flex flex-wrap gap-1.5" data-testid="mb-duration-chips">
                    {DURATION_CHIPS.map((n) => (
                        <button
                            key={n} type="button"
                            onClick={() => setDuration(n)}
                            data-testid={`mb-dur-${n}`}
                            className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${Number(duration) === n ? "bg-gold text-navy border-gold" : "border-white/25 text-white hover:border-gold/60"}`}
                        >
                            {n} min
                        </button>
                    ))}
                    <button
                        type="button" onClick={() => { setCustomOpen(true); setCustomVal(String(duration || "")); }}
                        data-testid="mb-dur-custom"
                        className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${DURATION_CHIPS.includes(Number(duration)) ? "border-white/25 text-white hover:border-gold/60" : "bg-gold text-navy border-gold"}`}
                    >
                        {DURATION_CHIPS.includes(Number(duration)) ? "Custom" : `${duration} min (custom)`}
                    </button>
                </div>
                {customOpen && (
                    <div className="mt-2 flex items-center gap-2" data-testid="mb-dur-custom-input">
                        <input
                            type="number" min={5} step={5} value={customVal}
                            onChange={(e) => setCustomVal(e.target.value)}
                            className="w-24 text-sm"
                        />
                        <span className="text-white/70 text-xs">minutes (5-min steps)</span>
                        <button type="button" onClick={setCustom} className="h-8 px-3 rounded-full bg-gold text-navy text-xs font-semibold">Apply</button>
                        <button type="button" onClick={() => setCustomOpen(false)} className="text-white/60 text-xs">Cancel</button>
                    </div>
                )}
            </div>

            {/* Live validity feedback against the day's returned slots */}
            {selectedSvc && time && (
                timeAvailable === false ? (
                    <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-2.5 text-[12px] text-amber-100" data-testid="mb-selection-conflict">
                        <strong>{time}</strong> isn't in the currently available slots for this service on {date}. Pick a nearby time from the list below, or change date.
                    </div>
                ) : timeAvailable === true ? (
                    <div className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 p-2.5 text-[12px] text-emerald-100" data-testid="mb-selection-ok">
                        <strong>{time}</strong> is available on {date}.
                    </div>
                ) : null
            )}

            {selectedSvc && selectedSvc.duration && duration && Number(selectedSvc.duration) !== Number(duration) && (
                <div className="text-[11px] text-white/60" data-testid="mb-selection-svc-note">
                    Note: the booking will use the service's configured duration ({selectedSvc.duration} min) — the calendar duration above is your visual selection.
                </div>
            )}
        </div>
    );
}
